/**
 * Banner 5 — Split hero: left copy + CTA, right 2×2 feature grid (label : line).
 * metadata = grid cells. Background is component fields → persisted as metadata_2[0] via transformPayload.
 */

/** Map API/Redux `metadata_2[0]` into flat form fields for admin. */
export function enrichBanner6SourceForForm(
  source: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!source || typeof source !== "object") return source;
  const m0 = Array.isArray(source.metadata_2) ? source.metadata_2[0] : null;
  if (!m0 || typeof m0 !== "object") return source;
  const row = m0 as Record<string, unknown>;
  return {
    ...source,
    background_image:
      (source.background_image as string) ?? (row.image as string) ?? "",
    alt_background_image:
      (source.alt_background_image as string) ??
      (row.alt_image as string) ??
      (row.mobile_alt_image as string) ??
      "",
  };
}

/** Strip non-DB fields and write single background row to `metadata_2`. */
export function buildBanner6PersistPayload(payload: Record<string, unknown>) {
  const img = String(payload.background_image ?? "").trim();
  const alt =
    String(payload.alt_background_image ?? "").trim() || "Hero background";
  const { background_image, alt_background_image, ...rest } = payload;
  return {
    ...rest,
    metadata_2:
      img.length > 0
        ? [
            {
              id: "1",
              image: img,
              alt_image: alt,
              mobile_image: "",
              mobile_alt_image: alt,
              priority: 1,
              status: true,
            },
          ]
        : [],
  };
}

export const BANNER_6_DEFAULT_GRID_ROWS = [
  {
    id: "1",
    icon: "",
    alt_icon: "Define",
    title: "DEFINE",
    description: "Problem → Scope",
    priority: 1,
    status: true,
  },
  {
    id: "2",
    icon: "",
    alt_icon: "Architect",
    title: "ARCHITECT",
    description: "End-to-end system design",
    priority: 2,
    status: true,
  },
  {
    id: "3",
    icon: "",
    alt_icon: "Build",
    title: "BUILD",
    description: "AI-assisted full-stack dev",
    priority: 3,
    status: true,
  },
  {
    id: "4",
    icon: "",
    alt_icon: "Deploy",
    title: "DEPLOY",
    description: "Ship. Monitor. Govern",
    priority: 4,
    status: true,
  },
];

export const BANNER_6_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Right column — feature grid",
  },

  DEFAULT_ROW: {
    id: "1",
    icon: "",
    alt_icon: "Feature",
    title: "LABEL",
    description: "Supporting line",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    {
      fieldName: "hero_section_height",
      label: "Minimum section height (px, optional — 0 = fit content)",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "0",
      defaultValue: 0,
    },
    {
      fieldName: "hero_container_max_width",
      label: "Content max width (px)",
      value: 1200,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1200",
      defaultValue: 1200,
    },
    {
      fieldName: "title_1",
      label: "Headline (wrap text in {} for the icon accent colour)",
      value: "Capstone: Build One System. Ship to Production",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Capstone: Build One System. Ship to Production",
    },
    {
      fieldName: "title_1_color",
      label: "Headline colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "subtitle",
      label: "Paragraph 1 (under headline)",
      value:
        "Choose a real-world problem from your domain - fintech, e-commerce, healthcare, or enterprise SaaS, and build a complete AI-native system from architecture to deployment.",
      valueType: "string",
      filedType: "TEXT_AREA",
      defaultValue:
        "Choose a real-world problem from your domain - fintech, e-commerce, healthcare, or enterprise SaaS, and build a complete AI-native system from architecture to deployment.",
      rows: 4,
    },
    {
      fieldName: "subtitle_color",
      label: "Body text colour",
      value: "rgba(255,255,255,0.92)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(255,255,255,0.92)",
    },
    {
      fieldName: "learners_description",
      label: "Paragraph 2",
      value:
        "Design, build, and deploy an end-to-end application integrating frontend, backend, AI systems, and agents - demonstrating true production-grade engineering capability.",
      valueType: "string",
      filedType: "TEXT_AREA",
      defaultValue:
        "Design, build, and deploy an end-to-end application integrating frontend, backend, AI systems, and agents - demonstrating true production-grade engineering capability.",
      rows: 4,
    },
    {
      fieldName: "icon_color",
      label:
        "Icon accent + headline {} highlight (fallback when no icon image)",
      value: "#F5C451",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#F5C451",
    },
    {
      fieldName: "button_color",
      label: "Submit button background",
      value: "#E85D04",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#E85D04",
    },
    {
      fieldName: "apply_now_text",
      label: "Submit button label",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Apply Now",
    },
    {
      fieldName: "apply_now_color",
      label: "Submit button text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "apply_now_link",
      label: "Submit button URL (Apply Now)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder:
        "/apply or https://… (used when Is CTA — Primary is off)",
      defaultValue: "",
    },
    {
      fieldName: "apply_now_is_cta",
      label: "Is CTA — Primary (lead popup)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "On = lead capture popup; off = open Submit button URL above",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "background_image",
      label: "Background image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Full-bleed hero image",
      defaultValue: "",
    },
    {
      fieldName: "alt_background_image",
      label: "Background image alt text",
      value: "Hero background",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Hero background",
    },
    {
      fieldName: "background_color",
      label: "Fallback background (no image)",
      value: "#0a0a0a",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#0a0a0a",
    },
    {
      fieldName: "status",
      label: "Banner status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Icon",
      fieldName: "icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "PNG/SVG (yellow artwork)",
      defaultValue: "",
    },
    {
      header: "Icon alt",
      fieldName: "alt_icon",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Label (before colon)",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Line (after colon)",
      fieldName: "description",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
